import React, { useState, useEffect } from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import { analyticsService } from "../../../services/analytics.service";
import GlobalDashboard from "./GlobalDashboard";
import ClientsTable from "./ClientsTable";
import Title from "../../common/Title";
import { TbChartBar, TbAlertCircle } from "react-icons/tb";
import { canAccessSuperUserAnalytics } from "../../../utils/authUtils";

function AllClientsAnalytics({ user, hideTitle }) {
  const [dateRange, setDateRange] = useState("oneMonth");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authorization
  if (!canAccessSuperUserAnalytics(user)) {
    return (
      <div>
        {!hideTitle && <Title text="All Clients Analytics" icon={<TbChartBar className="mb-1" />} />}
        <Alert variant="danger">
          <Alert.Heading className="d-flex align-items-center">
            <TbAlertCircle className="me-2" size={24} />
            Access Denied
          </Alert.Heading>
          <p className="mb-0">
            This page requires Super User or Product Team privileges.
            Please contact your administrator if you believe you should have access.
          </p>
        </Alert>
      </div>
    );
  }

  const getDateRangeParams = () => {
    const endDate = new Date();
    const startDate = new Date();

    switch (dateRange) {
      case "24hours":
        startDate.setDate(endDate.getDate() - 1);
        break;
      case "lastWeek":
        // Set to last complete week (Monday - Sunday)
        const currentDay = endDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysToLastMonday = currentDay === 0 ? 7 : currentDay + 6; // Days back to last Monday
        startDate.setDate(endDate.getDate() - daysToLastMonday);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6); // Sunday is 6 days after Monday
        endDate.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "lastMonth":
        // Set to last calendar month (first day to last day of previous month)
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(endDate.getMonth() - 1);
        endDate.setDate(new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate());
        endDate.setHours(23, 59, 59, 999);
        break;
      case "oneMonth":
        startDate.setDate(endDate.getDate() - 30);
        break;
      case "3months":
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case "6months":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "ytd":
        startDate.setMonth(0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "1year":
        // Set to last calendar year (Jan 1 - Dec 31 of previous year)
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      startDateObj: startDate,
      endDateObj: endDate,
    };
  };

  const formatDateRange = () => {
    const { startDateObj, endDateObj } = getDateRangeParams();
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return {
      start: startDateObj.toLocaleDateString('en-US', options),
      end: endDateObj.toLocaleDateString('en-US', options)
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = getDateRangeParams();

      // Fetch both dashboard stats and clients data in parallel
      const [dashboardResponse, clientsResponse] = await Promise.all([
        analyticsService.getSuperUserDashboard(params),
        analyticsService.getAllClientsAnalytics(params),
      ]);

      setDashboardStats(dashboardResponse.data);
      setClientsData(clientsResponse.data);
    } catch (err) {
      setError(err.response?.status === 403
        ? "You do not have permission to access this data."
        : "Failed to load analytics data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [dateRange]);

  const handleExportCSV = () => {
    if (!clientsData || clientsData.length === 0) return;

    // Create CSV content
    const headers = [
      "Client ID",
      "Client Name",
      "Total Executions",
      "Successful Executions",
      "Failed Executions",
      "Success Rate (%)",
      "Total API Calls",
      "Last Execution Time",
    ];

    const rows = clientsData.map((client) => [
      client.clientId,
      client.clientName,
      client.totalExecutions,
      client.successfulExecutions,
      client.failedExecutions,
      client.successRate.toFixed(2),
      client.totalApiCalls,
      client.lastExecutionTime || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `all-clients-analytics-${dateRange}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {!hideTitle && <Title text="All Clients Analytics" icon={<TbChartBar className="mb-1" />} />}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <Form.Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="24hours">Last 24 Hours</option>
              <option value="lastWeek">Last Week</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="oneMonth">Last 30 Days</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="ytd">Year to Date</option>
              <option value="1year">Last Year</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={8} className="d-flex align-items-end">
          <div className="text-muted">
            <strong>Showing data from:</strong> {formatDateRange().start} <strong>to</strong> {formatDateRange().end}
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          <TbAlertCircle className="me-2" />
          {error}
        </Alert>
      )}

      <GlobalDashboard stats={dashboardStats} loading={loading} />

      <ClientsTable
        clients={clientsData}
        loading={loading}
        onExportCSV={handleExportCSV}
      />
    </div>
  );
}

export default AllClientsAnalytics;
